import { Component, OnInit, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { VendorTsService } from '../../../../service/vendor/vendor.ts.service';
import { Subject, takeUntil } from 'rxjs';
import { from } from 'rxjs';

interface ChangeDetail {
  key: string;
  oldValue: any;
  newValue: any;
}

interface ChangeRequestResponse {
  message: {
    status: string;
    message: string;
    data: Array<{
      highlights_detail: string;
      creation: string;
    }>;
  }
}

const FIELD_ORDER = [
  // Company Information
  'company_title',
  'business_name',
  'vendor_code',
  'short_name',
  'plant',
  
  // Primary Address
  'primary_address.address_title',
  'primary_address.address_line_1',
  'primary_address.address_line_2',
  'primary_address.country',
  'primary_address.city',
  'primary_address.state',
  'primary_address.postal_code',
  'primary_address.fax',
  
  // MCA Details
  'ministry_of_corporate_affairsmca',
  'mca_number',
  'mca_document',
  
  // GST & Industry Details
  'gst_registered',
  'select_industry_type',
  'primary_address.tax_details.gstn',
  'primary_address.tax_details.upload_gst',
  'primary_address.tax_details.vat_registration_number', 
  'primary_address.tax_details.service_tax_registration_number',
  
  // Vendor Details
  'vendor_type',
  'propertiery_img',
  
  // Vendor Classification
  'vendor_classification',
  
  // Turnover Details
  'previous_year',
  'one_year_before_previous_year', 
  'two_years_before_previous_year',
  
  // Bank Details Array
  'primary_address.multiple_bank_detail.ifsc_code',
  'primary_address.multiple_bank_detail.account_no',
  'primary_address.multiple_bank_detail.account_holder__registered_name',
  'primary_address.multiple_bank_detail.plant_code',
  'primary_address.multiple_bank_detail.bank_name',
  'primary_address.multiple_bank_detail.branch_name',
  'primary_address.multiple_bank_detail.is_delete_bank',
  
  // Legal Details
  'pan_no',
  'pan_document',
  'msme_no',
  'msme_expiry_year',
  'msme_certificate',
  'cin_no',
  'upload_cin',
  'legal_name',
  'tin_no',
  'aadhar_no',
  'din_no',
  
  // Legal Documents
  'self_declaration_by_vendor',
  'company_registration',
  'other_documents',
  
  // Evaluation Documents
  'evaluation_sheet',
  'document_1_name', // Company Profile
  'document_1',
  'document_2_name', // Balance Sheet
  'document_2',
  'document_3_name', // Auditor Report
  'document_3',
  'document_4_name', // ISO Certificate
  'document_4',
  'document_5_name', // List of Equipment
  'document_5',
  'document_6_name', // Other
  'document_6',
  
  // Contact Details
  'vm_contact_detail.contacts',
  'vm_contact_detail.telephone',
  'vm_contact_detail.email_id',
  'vm_contact_detail.mobile_phone',
  'new_vm_contact_detail.is_delete_contact',
  
  // Rockman Contact Details
  'contact.rockmancontacts',
  'contact.first_name',
  'contact.last_name',
  'contact.rockman_contact_department',
  'contact.rockman_contact_function',
  'new_rockman_contact_detail.is_delete_person',
];


@Component({
  selector: 'app-change-detail-history',
  templateUrl: './change-detail-history.component.html',
  styleUrl: './change-detail-history.component.scss'
})
export class ChangeDetailHistoryComponent implements OnInit, OnDestroy {
  change_request_history_response: any[] = [];
  private destroy$ = new Subject<void>();
  bank_data: ChangeDetail[] = [];
  other_data: ChangeDetail[] = [];
  is_purchase_approver_1: boolean = false;
  is_finance_approver_2: boolean = false;
  isPurchase: boolean = false;

  constructor(
    public messageService: MessageService,
    private vendorService: VendorTsService,
  ) { }

  ngOnInit() {
    this.get_change_request_history_list();
  }

  get_change_request_history_list() {
    from(this.vendorService.get_change_request_history_list())
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res: ChangeRequestResponse) => {
          if (res.message.status === 'Success') {
            // Parse the highlights_detail JSON string for each record
            this.change_request_history_response = res.message.data.map(record => ({
              ...record,
              highlights_detail: JSON.parse(record.highlights_detail)
            }));
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: res.message.message
            });
            this.change_request_history_response = [];
          }
        },
        error: (error) => {
          console.error('Error fetching change request history:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch change request history'
          });
          this.change_request_history_response = [];
        }
      });
  }

  getFilteredDetails(highlights_detail: any[]) {
    this.bank_data = [];
    this.other_data = [];
    const ignoreFields = [
      'primary_address.contact_person.name1'
    ];
    
    if (!highlights_detail || !Array.isArray(highlights_detail)) {
      return [];
    }
    const filteredData = highlights_detail.filter((detail: any) => {
      const oldValueEmpty = detail.oldValue === null || detail.oldValue === '' || detail.oldValue === undefined;
      const newValueEmpty = detail.newValue === null || detail.newValue === '' || detail.newValue === undefined;
      const isIgnoredField = ignoreFields.includes(detail.key);      
      return !(oldValueEmpty && newValueEmpty) && !isIgnoredField && this.getShortKey(detail.key);
    });
    
    filteredData.forEach((res: any) => {
      if (res.key.startsWith("primary_address.multiple_bank_detail")) {
        this.bank_data.push(res);
      } else {
        this.other_data.push(res);
      }
    });
  
    let resultData: any[] = [];
    const hasBankChanges = this.bank_data.length > 0;
    const hasOtherChanges = this.other_data.length > 0;
  
    if (hasBankChanges && hasOtherChanges) {
      if (this.is_purchase_approver_1 || this.isPurchase) {
        resultData = this.other_data;
      } else if (this.is_finance_approver_2) {
        resultData = this.bank_data;
      } else {
        resultData = [...this.other_data, ...this.bank_data];
      }
    } else if (hasBankChanges && !hasOtherChanges) {
      resultData = this.bank_data;
    } else if (!hasBankChanges && hasOtherChanges) {
      resultData = this.other_data;
    }
  
    return resultData.sort((a, b) => {
      const aIndex = FIELD_ORDER.findIndex(field => 
        a.key.startsWith(field) || (field.includes('multiple_bank_detail') && a.key.includes('multiple_bank_detail'))
      );
      const bIndex = FIELD_ORDER.findIndex(field => 
        b.key.startsWith(field) || (field.includes('multiple_bank_detail') && b.key.includes('multiple_bank_detail'))
      );
      
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });
  }

  getShortKey(longKey: string): string | null {
    const keyMap: { [key: string]: string } = {
      "short_name": 'Short Name',
      "plant": 'Plant',
      "primary_address.address_title": "Address Title",
      "primary_address.address_line_1": "Street / House Number",
      "primary_address.address_line_2": "District",
      "primary_address.country": "Country",
      "primary_address.city": "City",
      "primary_address.state": "State/Province",
      "primary_address.postal_code": "Postal Code",
      "primary_address.fax": "Fax",
      "mca_number": 'MCA Number',
      "mca_document": "MCA Document",
      "select_industry_type": "Select Industry Type",
      "primary_address.tax_details.upload_gst": 'GST Document',
      'primary_address.tax_details.vat_registration_number': 'Vat Registration Number',
      'primary_address.tax_details.service_tax_registration_number': 'Service Tax Registration Number',
      "vendor_type": "Vendor Type",
      "vendor_classification": "Vendor Classification",
      "previous_year": "Previous Year",
      "one_year_before_previous_year": "One Year Before Previous Year",
      "two_years_before_previous_year": "Two Years Before Previous Year",
      'primary_address.multiple_bank_detail.account_holder__registered_name': 'Account Holder Name',
      'primary_address.multiple_bank_detail.account_no': 'Account No',
      'primary_address.multiple_bank_detail.plant_code': 'Plant Code',
      'primary_address.multiple_bank_detail.bank_name': 'Bank Name',
      'primary_address.multiple_bank_detail.bank_address': 'Bank Address',
      "primary_address.multiple_bank_detail.ifsc_code": 'Ifsc Code',
      'primary_address.multiple_bank_detail.branch_name': 'Branch Name',
      'primary_address.multiple_bank_detail.bank_city': 'Bank City',
      "primary_address.multiple_bank_detail.bank_region": 'Bank Region',
      "primary_address.multiple_bank_detail.cancelled_cheque": 'Bank Document',
      "primary_address.multiple_bank_detail.is_delete_bank": 'Delete Bank',
      "msme_no": "MSME No",
      "msme_expiry_year": "MSME Expiry Year",
      "msme_certificate": "MSME Certificate",
      "cin_no": "Cin No",
      "upload_cin": "CIN Document",
      "legal_name": "Legal Name",
      "tin_no": "Tin No",
      "aadhar_no": "Aadhar No",
      "din_no": "Din No",
      "company_registration": "Company Registration",
      "other_documents": "Other Documents",
      "evaluation_sheet": "Evaluation Sheet",
      "propertiery_img": "Profile Photo",
      "pan_document": "PAN Document",
      "document_1_name": "Company Profile",
      "document_1": "Company Profile Document",
      "document_2_name": "Balance Sheet",
      "document_2": "Balance Sheet",
      "document_3_name": "Auditor Report",
      "document_3": "Auditor Report",
      "document_4_name": "ISO Certificate ",
      "document_4": "ISO Certificate",
      "document_5_name": "List Of Equipment",
      "document_5": "List Of Equipment Document",
      "document_6_name": "Other",
      "document_6": "Other Document",
      "primary_address.address_type": "Address Type",
      "new_vm_contact_detail.vm_name": "VM Name",
      "new_vm_contact_detail.telephone": "Telephone",
      "new_vm_contact_detail.email_id": "Email ID",
      "new_vm_contact_detail.mobile_phone": "Mobile Phone",
      "new_vm_contact_detail.is_delete_contact": "Delete Contact",
      "new_rockman_contact_detail.first_name": "First Name",
      "new_rockman_contact_detail.last_name": "Last Name",
      "new_rockman_contact_detail.rockman_contact_department": "Department",
      "new_rockman_contact_detail.rockman_contact_function": "Function",
      "new_rockman_contact_detail.is_delete_person":"Delete Contact Person",
      "financial_previous_year":"Turn Over Last Year",
      "financial_one_year_before_previous_year":"Turn Over Last Second Year",
      "financial_two_years_before_previous_year":"Turn Over Last Third Year",
      "primary_address.state1":"State",
      "primary_address.tax_details.gstn":"GST Number",
      "ministry_of_corporate_affairsmca":"Ministry Of Corporate Affairs(MCA)",
      "pan_no":"Pan Number",
      "primary_address.contact_person.mobile_no":"Mobile Number",
      "primary_address.contact_person.e_mail_id":"Email",
      "primary_address.contact_person.phone_no":"Phone Number"
    };

    const dynamicKeyMatchForBank = longKey.match(/primary_address\.multiple_bank_detail\.(\d+)\.(.+)/);
    if (dynamicKeyMatchForBank) {
      const index = dynamicKeyMatchForBank[1]; 
      const subKey = dynamicKeyMatchForBank[2]; 

      const mappedKey = keyMap[`primary_address.multiple_bank_detail.${subKey}`];
      if (mappedKey) {
        return `Bank ${parseInt(index) + 1}: ${mappedKey}`;
      }
    }

    const dynamicKeyForVM = longKey.match(/new_vm_contact_detail\.(\d+)\.(.+)/);
    if (dynamicKeyForVM) {
      const index = dynamicKeyForVM[1]; 
      const subKey = dynamicKeyForVM[2];

      const mappedKey = keyMap[`new_vm_contact_detail.${subKey}`];
      if (mappedKey) {
        return `VM Contact ${parseInt(index) + 1}: ${mappedKey}`;
      }
    }

    const dynamicKeyMatchForRC = longKey.match(/new_rockman_contact_detail\.(\d+)\.(.+)/);
    if (dynamicKeyMatchForRC) {
      const index = dynamicKeyMatchForRC[1]; 
      const subKey = dynamicKeyMatchForRC[2]; 

      const mappedKey = keyMap[`new_rockman_contact_detail.${subKey}`];
      if (mappedKey) {
        return `Rockman Contact ${parseInt(index) + 1}: ${mappedKey}`;
      }
    }

    return keyMap[longKey] || longKey;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}